import { useState, useEffect } from 'react'
import { Tour } from '../Types'
import { useUpdateUserCompletedProductTour } from 'hooks/queries/users/useUpdateUserCompletedProductTour'

type AvailableTour = Tour | null
type Listener = (tour: AvailableTour) => void

class ProductTourManager {
  private availableTour: AvailableTour = null
  private listeners: Set<Listener> = new Set()

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.availableTour))
  }

  public getAvailableTour(): AvailableTour {
    return this.availableTour
  }

  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    listener(this.availableTour)
    return () => this.listeners.delete(listener)
  }

  public registerProductTour(tourKey: Tour): void {
    this.availableTour = tourKey
    this.notify()
  }

  public unregisterProductTour(tourKey: Tour): void {
    if (this.availableTour === tourKey) {
      this.availableTour = null
      this.notify()
    }
  }
}

const productTourManager = new ProductTourManager()

export function useProductTourObserver() {
  const [availableTour, setAvailableTour] = useState<AvailableTour>(productTourManager.getAvailableTour())
  const { mutate: updateProductTour } = useUpdateUserCompletedProductTour()

  useEffect(() => {
    const unsubscribe = productTourManager.subscribe(setAvailableTour)
    return () => unsubscribe()
  }, [])

  const restartProductTour = (tour: Tour) => {
    updateProductTour({ tour, step: 0 })
  }

  return {
    availableTour,
    restartProductTour,
    registerProductTour: productTourManager.registerProductTour.bind(productTourManager),
    unregisterProductTour: productTourManager.unregisterProductTour.bind(productTourManager),
  }
}
